<script lang="ts" setup>
import { computed, onMounted, ref, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router/composables'
import { useInfiniteScroll } from '@vueuse/core'
import { tagsConfig as rocnovelConfig } from './configs/rocnovel'
import { tagsConfig as ocereadConfig } from './configs/oceread'
import { tagsConfig as talewaveConfig } from './configs/talewave'
import { tagsConfig as ficmoonConfig } from './configs/ficmoon'
import { tagsConfig as novelarcConfig } from './configs/novelarc'
import { tagsConfig as storyellaConfig } from './configs/storyella'
import { tagsConfig as florastoryConfig } from './configs/florastory'
import { tagsConfig as silktaleConfig } from './configs/silktale'
import { tagsConfig as pearlaConfig } from './configs/pearla'
import { tagsConfig as fictoraConfig } from './configs/fictora'
import { tagsConfig as marinovelConfig } from './configs/marinovel'
import { tagsConfig as flipnovelConfig } from './configs/flipnovel'
import { tagsConfig as moonpageConfig } from './configs/moonpage'
import { tagsConfig as storlynConfig } from './configs/storlyn'
import { tagsConfig as novarisConfig } from './configs/novaris'
import TagsPageFlipnovel from './components/TagsPage.flipnovel.vue'
import TagsPageDefault from './components/TagsPage.default.vue'
import TagsPageTalewave from './components/TagsPage.talewave.vue'
import { api } from '@/api'
import { loadBrandComponent, loadBrandConfig } from '@/utils/loadBrandConfig'
import { READER_ROUTE_NAME } from '@/utils/readerRouteName'

// 🔥 加载当前品牌配置（构建时 tree-shaking）
const config = loadBrandConfig({
  rocnovel: rocnovelConfig,
  oceread: ocereadConfig,
  talewave: talewaveConfig,
  ficmoon: ficmoonConfig,
  novelarc: novelarcConfig,
  storyella: storyellaConfig,
  florastory: florastoryConfig,
  silktale: silktaleConfig,
  pearla: pearlaConfig,
  fictora: fictoraConfig,
  marinovel: marinovelConfig,
  flipnovel: flipnovelConfig,
  moonpage: moonpageConfig,
  storlyn: storlynConfig,
  novaris: novarisConfig,
})

const TagsPage = loadBrandComponent({
  rocnovel: TagsPageDefault,
  oceread: TagsPageDefault,
  talewave: TagsPageTalewave,
  ficmoon: TagsPageDefault,
  novelarc: TagsPageDefault,
  storyella: TagsPageDefault,
  florastory: TagsPageDefault,
  silktale: TagsPageDefault,
  pearla: TagsPageDefault,
  fictora: TagsPageDefault,
  marinovel: TagsPageDefault,
  flipnovel: TagsPageFlipnovel,
  moonpage: TagsPageFlipnovel,
  storlyn: TagsPageFlipnovel,
  novaris: TagsPageFlipnovel,
})

const route = useRoute()
const loading = ref(true)
const tabLoading = ref(false)
const router = useRouter()
const tagsList = ref<Array<{ id: string; isHot: boolean; resourceList: object; tagName: string }>>([])
const activeTab = ref(0)
const contentTabs = [config.texts.mostPopular, config.texts.newArrival]
const activeContentTab = ref(0)
const booksData = ref<any[]>([])

const page = ref(1)
const total = ref<number>(0)
const fetching = ref(false)
const ending = ref(false)
const error = ref<any>()
const pagination = computed(() => ({
  pageIndex: page.value,
  pageSize: 20,
}))

const filteredBooks = computed(() => {
  return booksData.value.filter((book) => {
    const selectedContentTab = contentTabs[activeContentTab.value]
    return book.contentType === selectedContentTab
  })
})

onMounted(async () => {
  loading.value = true
  try {
    await getAllTags()
    const tagId = route.query.tagId as string
    if (tagId) {
      const index = tagsList.value.findIndex(tag => tag.id === tagId)
      if (index !== -1) {
        activeTab.value = index
        await fetchBooksByTag()
      }
    }
    else {
      await fetchBooksByTag()
    }
  }
  finally {
    loading.value = false
  }
})

watch([activeTab, activeContentTab], async () => {
  tabLoading.value = true
  resetScrollPosition()
  booksData.value = []
  page.value = 1
  ending.value = false
  fetching.value = false
  await fetchBooksByTag()
  tabLoading.value = false
})

function gotoBook(id: string, firstChapterId?: string) {
  router.push({
    name: READER_ROUTE_NAME,
    params: { contentId: id, chapterId: firstChapterId || '' },
  })
}

async function getAllTags() {
  try {
    loading.value = true
    const { data } = await api.get('/tag/getAllTag')
    tagsList.value = data
  }
  catch (error) {
    console.error('获取标签失败:', error)
  }
  finally {
    loading.value = false
  }
}

async function fetchBooksByTag() {
  if (fetching.value || ending.value)
    return
  fetching.value = true

  if (tagsList.value.length === 0) {
    fetching.value = false
    return
  }

  try {
    const tagId = tagsList.value[activeTab.value].id
    const orderBy = activeContentTab.value + 1
    const { data } = await api.get('/content/getTagContentList', {
      params: {
        tagId,
        orderBy,
        pageIndex: page.value,
        pageSize: pagination.value.pageSize,
      },
    })

    const newBooks = data.records.map((item: any) => ({
      id: item.id,
      contentName: item.contentName,
      contentCoverUrl: item.contentCoverUrl,
      introduce: item.introduce,
      tags: item.tagList || [],
      contentType: orderBy === 1 ? config.texts.mostPopular : config.texts.newArrival,
      viewUsers: item.viewUsers,
      contentClassName: item.contentClassName,
      firstChapterId: item.firstChapter?.id,
    }))

    booksData.value = [...booksData.value, ...newBooks]
    total.value = data.total

    if (page.value >= data.pages)
      ending.value = true
    else
      page.value += 1
  }
  catch (err) {
    console.error('获取书籍失败:', err)
    error.value = err
  }
  finally {
    fetching.value = false
  }
}

const contentContainer = ref<HTMLElement | null>(null)
const infiniteScrollProps = useInfiniteScroll(
  contentContainer,
  async () => {
    if (fetching.value || ending.value || error.value)
      return
    await fetchBooksByTag()
  },
  { distance: 50 },
)

function resetScrollPosition() {
  contentContainer.value?.scrollTo({ top: 0 })
}

function setContentContainer(el: HTMLElement | null) {
  contentContainer.value = el
}
</script>

<template>
  <component
    :is="TagsPage"
    :loading="loading"
    :config="config"
    :active-tab="activeTab"
    :tags-list="tagsList"
    :active-content-tab="activeContentTab"
    :content-tabs="contentTabs"
    :filtered-books="filteredBooks"
    :tab-loading="tabLoading"
    :ending="ending"
    :fetching="fetching"
    :books-data="booksData"
    :infinite-scroll-props="infiniteScrollProps"
    :set-content-container="setContentContainer"
    :goto-book="gotoBook"
    @update:active-tab="(newVal) => activeTab = newVal"
    @update:active-content-tab="(newVal) => activeContentTab = newVal"
  />
</template>
